import { Button } from "@/components/ui/button";
import { z } from "zod";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Alert, AlertTitle } from "@/components/ui/alert";
import { ExclamationTriangleIcon } from "@radix-ui/react-icons";
import { userValidator } from "@shared/validators/user-validator";
import { PasswordInput } from "@/components/ui/input-password";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { useUserMutation } from "@/api/mutations/UserMutations";
import { useState } from "react";

type Props = {
    user?: z.infer<typeof userValidator.findUser>;
    mode?: "create" | "edit";
    onSuccess?: () => void;
};

const UserManagementForm = ({ user, mode = "create", onSuccess }: Props) => {
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState("");

    const isEditing = mode === "edit" && user;

    const userMutation = useUserMutation(
        (open) => {
            if (!open) onSuccess?.();
        },
        user?.id,
        setIsLoading,
        setError
    );

    // Create a more flexible schema for the form
    const FormSchema = isEditing
        ? userValidator.updateUser.extend({ id: z.string() })
        : userValidator.createUser.extend({
              position: z.string().min(1, "Position is required"),
              department: z.string().min(1, "Department is required"),
          });

    const form = useForm<z.infer<typeof FormSchema>>({
        resolver: zodResolver(FormSchema),
        mode: "onSubmit",
        defaultValues: {
            id: user?.id ?? "",
            name: user?.name ?? "",
            email: user?.email ?? "",
            position: user?.position ?? "Employee",
            department: user?.department ?? "General",
            password: "",
            role: user?.role ?? "user",
            emailVerified: user?.emailVerified ?? false,
            twoFactorEnabled: user?.twoFactorEnabled ?? false,
            // Required fields for createUser schema
            image: user?.image ?? null,
            banReason: user?.banReason ?? null,
            banned: user?.banned ?? null,
            banExpires: user?.banExpires ?? null,
        },
    });

    const handleSubmit = (data: any) => {
        console.log("Form submitted with data:", data);

        // Clean up the data for submission
        const submitData = isEditing
            ? { id: user.id, ...data }
            : {
                  ...data,
                  // Ensure required fields are present
                  position: data.position || "Employee",
                  department: data.department || "General",
              };

        console.log("Submitting data:", submitData);
        userMutation.mutate(submitData);
    };

    // Debug form errors
    const formErrors = form.formState.errors;
    if (Object.keys(formErrors).length > 0) {
        console.log("Form validation errors:", formErrors);
    }

    return (
        <Form {...form}>
            <form
                onSubmit={form.handleSubmit(handleSubmit, (errors) => {
                    console.log("Form submission errors:", errors);
                })}
            >
                <div className="max-h-[75vh] overflow-y-auto px-1 pb-6 2xl:max-h-[82vh]">
                    {error && (
                        <Alert variant="destructive" className="mb-4">
                            <div className="flex items-center gap-2">
                                <ExclamationTriangleIcon className="h-4 w-4" />
                                <AlertTitle className="mb-0">{error}</AlertTitle>
                            </div>
                        </Alert>
                    )}

                    <div className="flex w-full flex-wrap gap-8">
                        <div className="flex-1 space-y-4">
                            {/* Basic Information */}
                            <div className="space-y-3">
                                <FormField
                                    control={form.control}
                                    name="name"
                                    render={({ field }) => (
                                        <FormItem>
                                            <FormLabel className="text-sm font-medium">Full Name *</FormLabel>
                                            <FormControl>
                                                <Input
                                                    {...field}
                                                    value={field.value || ""}
                                                    placeholder="Enter full name"
                                                />
                                            </FormControl>
                                            <FormMessage />
                                        </FormItem>
                                    )}
                                />

                                <FormField
                                    control={form.control}
                                    name="email"
                                    render={({ field }) => (
                                        <FormItem>
                                            <FormLabel className="text-sm font-medium">Email Address *</FormLabel>
                                            <FormControl>
                                                <Input
                                                    {...field}
                                                    value={field.value || ""}
                                                    type="email"
                                                    placeholder="user@example.com"
                                                    disabled={!!isEditing} // Email shouldn't be changed after creation
                                                />
                                            </FormControl>
                                            <FormMessage />
                                        </FormItem>
                                    )}
                                />

                                {!isEditing && (
                                    <FormField
                                        control={form.control}
                                        name="password"
                                        render={({ field }) => (
                                            <FormItem>
                                                <FormLabel className="text-sm font-medium">Password *</FormLabel>
                                                <FormControl>
                                                    <PasswordInput
                                                        {...field}
                                                        value={field.value || ""}
                                                        placeholder="Enter password"
                                                    />
                                                </FormControl>
                                                <FormMessage />
                                            </FormItem>
                                        )}
                                    />
                                )}
                            </div>
                            <div className="grid grid-cols-2 gap-4">
                                <FormField
                                    control={form.control}
                                    name="position"
                                    render={({ field }) => (
                                        <FormItem>
                                            <FormLabel className="text-sm font-medium">Position *</FormLabel>
                                            <FormControl>
                                                <Input
                                                    {...field}
                                                    value={field.value || ""}
                                                    placeholder="e.g. Software Engineer"
                                                    required
                                                />
                                            </FormControl>
                                            <FormMessage />
                                        </FormItem>
                                    )}
                                />

                                <FormField
                                    control={form.control}
                                    name="department"
                                    render={({ field }) => (
                                        <FormItem>
                                            <FormLabel className="text-sm font-medium">Department *</FormLabel>
                                            <FormControl>
                                                <Input
                                                    {...field}
                                                    value={field.value || ""}
                                                    placeholder="e.g. Engineering"
                                                    required
                                                />
                                            </FormControl>
                                            <FormMessage />
                                        </FormItem>
                                    )}
                                />
                            </div>
                        </div>

                        <div className="flex-1 space-y-5">
                            {/* Role & Permissions */}
                            <div className="space-y-3">
                                <FormField
                                    control={form.control}
                                    name="role"
                                    render={({ field }) => (
                                        <FormItem>
                                            <FormLabel className="text-sm font-medium">Role *</FormLabel>
                                            <FormControl>
                                                <Select
                                                    onValueChange={field.onChange}
                                                    defaultValue={field.value || "user"}
                                                >
                                                    <SelectTrigger className="h-9 self-start text-start">
                                                        <SelectValue placeholder="Select a role" />
                                                    </SelectTrigger>
                                                    <SelectContent>
                                                        <SelectItem value="admin">
                                                            <div className="flex flex-col">
                                                                <span>Admin</span>
                                                                <span className="text-xs text-muted-foreground">
                                                                    Full system access and user management
                                                                </span>
                                                            </div>
                                                        </SelectItem>
                                                        <SelectItem value="manager">
                                                            <div className="flex flex-col">
                                                                <span>Manager</span>
                                                                <span className="text-xs text-muted-foreground">
                                                                    Can manage team members and content
                                                                </span>
                                                            </div>
                                                        </SelectItem>
                                                        <SelectItem value="user">
                                                            <div className="flex flex-col">
                                                                <span>User</span>
                                                                <span className="text-xs text-muted-foreground">
                                                                    Standard user access
                                                                </span>
                                                            </div>
                                                        </SelectItem>
                                                    </SelectContent>
                                                </Select>
                                            </FormControl>
                                            <FormMessage />
                                        </FormItem>
                                    )}
                                />
                            </div>

                            {/* Security Settings */}
                            <div className="flex flex-col gap-4">
                                <FormField
                                    control={form.control}
                                    name="emailVerified"
                                    render={({ field }) => (
                                        <FormItem className="flex flex-row items-start space-x-3 space-y-0">
                                            <FormControl>
                                                <Checkbox checked={field.value || false} onCheckedChange={field.onChange} />
                                            </FormControl>
                                            <div className="flex flex-col gap-2">
                                                <FormLabel className="text-sm font-medium leading-none">Email Verified</FormLabel>
                                                <p className="text-xs text-muted-foreground">
                                                    User's email address has been verified
                                                </p>
                                            </div>
                                        </FormItem>
                                    )}
                                />

                                <FormField
                                    control={form.control}
                                    name="twoFactorEnabled"
                                    render={({ field }) => (
                                        <FormItem className="flex flex-row items-start space-x-3 space-y-0">
                                            <FormControl>
                                                <Checkbox checked={field.value || false} onCheckedChange={field.onChange} />
                                            </FormControl>
                                            <div className="flex flex-col gap-2">
                                                <FormLabel className="text-sm font-medium leading-none">
                                                    Two-Factor Authentication
                                                </FormLabel>
                                                <p className="text-xs text-muted-foreground">
                                                    Require 2FA for enhanced security
                                                </p>
                                            </div>
                                        </FormItem>
                                    )}
                                />
                            </div>
                        </div>
                    </div>
                </div>

                <div className="flex justify-end space-x-2 border-t pt-4">
                    <Button type="submit" disabled={isLoading}>
                        {isLoading
                            ? isEditing
                                ? "Updating..."
                                : "Creating..."
                            : isEditing
                              ? "Update User"
                              : "Create User"}
                    </Button>
                </div>
            </form>
        </Form>
    );
};

export default UserManagementForm;
